import { useCallback, useRef, useState } from "react";
import { MoveHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Reveal } from "./Reveal";

const AFTER_IMG =
  "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?fm=jpg&q=80&w=2200&auto=format&fit=crop";
const BEFORE_FILTER = "brightness(0.48) saturate(0.3) contrast(0.85) sepia(0.22)";

export const BeforeAfter = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(58);

  const update = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos(Math.min(96, Math.max(4, ((clientX - r.left) / r.width) * 100)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    update(e.clientX);
  };
  const onPointerMove = (e) => {
    if (dragging.current) update(e.clientX);
  };
  const stop = () => {
    dragging.current = false;
  };
  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") setPos((p) => Math.max(4, p - 4));
    if (e.key === "ArrowRight") setPos((p) => Math.min(96, p + 4));
  };

  return (
    <section id="editing" className="relative bg-[#0D0F12] px-6 py-28 lg:px-12 lg:py-40" data-testid="beforeafter-section">
      <div className="mx-auto max-w-7xl">
        <div className="grid items-end gap-8 lg:grid-cols-12">
          <Reveal className="lg:col-span-8">
            <p className="font-code text-[11px] uppercase tracking-[0.35em] text-[#D6705A]">{t("beforeafter.overline")}</p>
            <h2 className="mt-4 font-display text-4xl font-light leading-tight text-[#F9F8F6] sm:text-5xl lg:text-6xl">
              {t("beforeafter.h2pre")} <em className="italic text-[#D6705A]">{t("beforeafter.h2em")}</em>.
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-4">
            <p className="text-sm leading-relaxed text-[#A0A0A0] md:text-base">
              {t("beforeafter.text")}
            </p>
            <p className="mt-6 font-display text-lg font-light italic leading-snug text-[#C9C7C3] sm:text-xl" data-testid="beforeafter-closing">
              {t("beforeafter.closing")}
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="mt-14">
          <div
            ref={ref}
            role="slider"
            aria-label={t("beforeafter.aria")}
            aria-valuenow={Math.round(pos)}
            aria-valuemin={0}
            aria-valuemax={100}
            tabIndex={0}
            onKeyDown={onKeyDown}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={stop}
            onPointerCancel={stop}
            className="relative aspect-[16/10] cursor-ew-resize touch-none select-none overflow-hidden focus:outline-none focus-visible:ring-1 focus-visible:ring-[#D6705A] sm:aspect-[16/8]"
            data-testid="beforeafter-slider"
          >
            <img
              src={AFTER_IMG}
              alt={t("beforeafter.imgAlt")}
              draggable={false}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover"
              data-testid="beforeafter-after"
            />
            <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }} data-testid="beforeafter-before">
              <img
                src={AFTER_IMG}
                alt=""
                aria-hidden
                draggable={false}
                className="absolute inset-0 h-full w-full object-cover"
                style={{ filter: BEFORE_FILTER, transform: "rotate(-2.5deg) scale(1.12)" }}
              />
            </div>
            <div className="absolute inset-y-0 w-px bg-[#F9F8F6]/90" style={{ left: `${pos}%` }}>
              <span
                className="absolute left-1/2 top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/40 bg-[#0D0F12]/70 backdrop-blur-md"
                data-testid="beforeafter-handle"
              >
                <MoveHorizontal className="h-5 w-5 text-[#F9F8F6]" />
              </span>
            </div>
            <span className="absolute left-5 top-5 font-code text-[10px] uppercase tracking-[0.3em] text-[#F9F8F6]/90" data-testid="beforeafter-label-before">
              {t("beforeafter.before")}
            </span>
            <span className="absolute right-5 top-5 font-code text-[10px] uppercase tracking-[0.3em] text-[#F9F8F6]/90" data-testid="beforeafter-label-after">
              {t("beforeafter.after")}
            </span>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
